import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDocs, collection } from 'firebase/firestore';
import { db, firebaseConfig } from '../firebase/config';

/**
 * SuperAdmin tarafından yeni bir Alt Yönetici kaydetmek.
 * İkincil bir Firebase App oluşturularak mevcut oturumun düşmesi engellenir.
 */
export const createAdminUser = async (adminData) => {
  const secondaryApp = initializeApp(firebaseConfig, `SecondaryApp_${Date.now()}`);
  const secondaryAuth = getAuth(secondaryApp);

  const sanitizedUsername = (adminData.username || adminData.email || '').toLowerCase().trim();
  const generatedEmail = sanitizedUsername.includes('@') ? sanitizedUsername : `${sanitizedUsername}@hasimoglu.local`;

  try {
    // 1. Yeni hesabı oluştur
    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      generatedEmail,
      adminData.password
    );

    const newUid = userCredential.user.uid;

    // 2. Yeni kullanıcının bilgilerini asıl Firestore'a kaydet
    await setDoc(doc(db, 'users', newUid), {
      username: sanitizedUsername,     // Yönetim panelinde ID olarak gösterilir
      email: generatedEmail,
      displayName: adminData.displayName.trim(),
      phone: adminData.phone.trim(),
      rawPassword: adminData.password, // SUPER ADMIN ICIN ACİK SIFRE GORUNUMU
      role: adminData.role || 'admin',
      isActive: true,
      assignedBranches: adminData.assignedBranches || [],
      createdAt: serverTimestamp(),
      createdBySuperAdmin: adminData.isSuperAdminAction || false,
      parentAdminId: adminData.parentAdminId || null
    });

    // 3. İkincil Firebase App'i temizle
    await secondaryAuth.signOut();
    await deleteApp(secondaryApp);

    return newUid;
  } catch (error) {
    // Hata durumunda da temizlik yap
    try {
      await deleteApp(secondaryApp);
    } catch (_) {}
    throw error;
  }
};

/**
 * Tüm kullanıcıları listeler (SuperAdmin paneli için)
 */
export const getAllUsers = async () => {
  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);
  const users = [];
  snapshot.forEach(doc => {
    users.push({ id: doc.id, ...doc.data() });
  });
  return users;
};

/**
 * Kullanıcının şubelerini veya bilgilerini günceller
 */
export const updateUserBranches = async (uid, newBranches) => {
  await setDoc(doc(db, 'users', uid), {
    assignedBranches: newBranches
  }, { merge: true });
};

/**
 * Kullanıcı statüsünü değiştirir (Askıya Al / Aktifleştir).
 * Askıya alınan kullanıcı AuthContext üzerinden Firebase'den anında düşürülür.
 */
export const toggleUserStatus = async (uid, currentStatus) => {
  const newStatus = !currentStatus;
  await setDoc(doc(db, 'users', uid), {
    isActive: newStatus
  }, { merge: true });
};

/**
 * Kullanıcının dinamik yetki matrisini (şalterlerini) günceller
 */
export const updateUserPermissions = async (uid, permissions) => {
  await setDoc(doc(db, 'users', uid), {
    permissions: permissions
  }, { merge: true });
};

/**
 * SuperAdmin tarafından mevcut bir yöneticinin şifresini değiştirmek.
 * Firebase Auth güvenlik kurallarına takılmamak için İkincil Firebase App kullanılır.
 */
export const updateAdminPassword = async (email, currentPassword, newPassword, uid) => {
  const { signInWithEmailAndPassword, updatePassword } = require('firebase/auth');
  const secondaryApp = initializeApp(firebaseConfig, `SecondaryApp_Update_${Date.now()}`);
  const secondaryAuth = getAuth(secondaryApp);

  try {
    // 1. Hedef kullanıcının mevcut bilgileriyle ikincil session aç
    const userCredential = await signInWithEmailAndPassword(secondaryAuth, email, currentPassword);
    
    // 2. Auth tarafındaki şifresini değiştir
    await updatePassword(userCredential.user, newPassword);

    // 3. Firestore'daki açık şifreyi güncelle
    await setDoc(doc(db, 'users', uid), {
      rawPassword: newPassword
    }, { merge: true });

    // 4. Temizlik
    await secondaryAuth.signOut();
    await deleteApp(secondaryApp);
  } catch (error) {
    try {
      await deleteApp(secondaryApp);
    } catch (_) {}
    throw error;
  }
};
